import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  ShopUser,
  useDeleteUserMutation,
  useGetRolesQuery,
  useGetUsersQuery,
  useSaveUserMutation,
  useUpdateUserMutation,
} from '../../api/auth/AuthApi';
import { currentUser } from '../../utils/auth';
import { useSnackbar } from '../../utils/snackbar';
import { readable } from '../../utils/text';

/** Who can log in, and as what. Owner only. */
const Users = () => {
  const { openSnackbar } = useSnackbar();
  const me = currentUser();
  const { data: users = [] } = useGetUsersQuery();
  // Whatever roles the shop has made, not a list fixed in the code.
  const { data: roles = [] } = useGetRolesQuery();
  const [saveUser, { isLoading: saving }] = useSaveUserMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: removing }] = useDeleteUserMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ShopUser | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ShopUser | null>(null);
  const [form, setForm] = useState<ShopUser>({ userName: '', role: '' });

  const openForm = (user?: ShopUser) => {
    setEditing(user ?? null);
    // A new login starts on the least powerful role there is, never the first.
    const fallback = roles.find((role) => role.roleCode === 'BILLER') ?? roles[roles.length - 1];
    setForm(user ? { ...user, password: '' } : { userName: '', role: fallback?.roleCode ?? '', password: '', fullName: '' });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing?.id) {
        await updateUser({ ...form, id: editing.id }).unwrap();
      } else {
        await saveUser(form).unwrap();
      }
      openSnackbar(`User ${editing ? 'updated' : 'created'}`, 'success');
      setOpen(false);
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not save the user', 'error');
    }
  };

  const remove = async () => {
    if (!removeTarget?.id) return;
    try {
      await deleteUser(removeTarget.id).unwrap();
      openSnackbar(`${removeTarget.userName} can no longer sign in`, 'success');
      setRemoveTarget(null);
    } catch (error: any) {
      // Refused when it is the only owner left - the shop would lock itself out.
      openSnackbar(error?.data?.message || 'Could not remove the login', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Who can log in
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Counter staff can bill and look up stock. They cannot cancel bills, change the masters or see reports.
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<PersonAddAlt1Icon />} onClick={() => openForm()}>
          Add User
        </Button>
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>User Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  {user.userName}
                  {user.userName === me?.userName && (
                    <Typography variant="caption" color="text.secondary">
                      {' '}
                      (you)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{user.fullName || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={user.role === 'OWNER' ? 'primary' : 'default'}
                    label={readable(user.roleName || user.role)}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openForm(user)} aria-label="Edit">
                    <EditTwoToneIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setRemoveTarget(user)} aria-label="Remove">
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberRoundedIcon color="warning" />
          <Typography fontWeight={700} fontSize="1rem">
            Remove this login?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" fontWeight={700} color="text.primary">
              {removeTarget?.fullName || removeTarget?.userName}
            </Box>{' '}
            will not be able to sign in. Bills and purchases they entered stay exactly as they are.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setRemoveTarget(null)}>
            No, Keep It
          </Button>
          <LoadingButton variant="contained" color="error" size="small" loading={removing} onClick={remove}>
            Yes, Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.userName}` : 'Add User'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="User Name"
              size="small"
              disabled={!!editing}
              value={form.userName}
              onChange={(event) => setForm({ ...form, userName: event.target.value })}
            />
            <TextField label="Full Name" size="small" value={form.fullName || ''} onChange={(event) => setForm({ ...form, fullName: event.target.value })} />
            <TextField
              label={editing ? 'New Password (leave blank to keep)' : 'Password'}
              size="small"
              type="password"
              value={form.password || ''}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
            <TextField
              select
              label="Role"
              size="small"
              value={form.role}
              helperText="Roles and their rights are set on the Roles tab"
              onChange={(event) => setForm({ ...form, role: String(event.target.value) })}
            >
              {roles.map((role) => (
                <MenuItem key={role.roleCode} value={role.roleCode}>
                  {role.roleName}
                  {role.description ? ` - ${role.description}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={saving || updating} onClick={save}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
